import { Link } from "react-router";
import { Alerta, BarraProgreso, Badge, Button, Card, Dialog, Galeria, Recorrido, Row, Spinner, Stack } from "@/components";
import type { Novedad } from "@/lib/api";
import { fotosGaleria, textoCambio } from "@/lib/avance";
import { formatFecha } from "@/lib/format";
import { ControlInterno } from "./ControlInterno";
import { DetallePaso } from "./DetallePaso";
import { FormularioCumplido } from "./FormularioCumplido";
import { FormularioInicio } from "./FormularioInicio";
import { FormularioNovedad } from "./FormularioNovedad";
import { useAvanceTab } from "./useAvanceTab";

function TarjetaNovedad({ fid, novedad, onEditar, onBorrar }: { fid: number; novedad: Novedad; onEditar?: () => void; onBorrar?: () => void }) {
  return (
    <Card>
      <article aria-labelledby={`novedad-${novedad.id}`}>
        <Stack espacio={3}>
          <Stack espacio={1}>
            <span className="text-chico text-texto-suave">{formatFecha(novedad.fecha)}</span>
            <h3 id={`novedad-${novedad.id}`} className="text-subtitulo font-medium">
              {novedad.titulo}
            </h3>
          </Stack>
          {novedad.texto && <p className="whitespace-pre-line text-texto-suave">{novedad.texto}</p>}
          {novedad.avances.length > 0 && (
            <Row envolver espacio={2}>
              {novedad.avances.map((a) => (
                <Badge key={a.etapa_id} tono="primario">
                  {textoCambio(a)}
                </Badge>
              ))}
            </Row>
          )}
          <Galeria etiqueta={`Fotos de ${novedad.titulo}`} fotos={fotosGaleria(fid, novedad)} />
          {onEditar && onBorrar && (
            <Row espacio={2}>
              <Button variante="secundario" tamanio="sm" aria-label={`Editar novedad: ${novedad.titulo}`} onClick={onEditar}>
                Editar
              </Button>
              <Button variante="fantasma" tamanio="sm" aria-label={`Borrar novedad: ${novedad.titulo}`} onClick={onBorrar}>
                Borrar
              </Button>
            </Row>
          )}
        </Stack>
      </article>
    </Card>
  );
}

const RUTA_HITOS = "/configuracion/avance";

export function AvanceTab() {
  const t = useAvanceTab();
  const a = t.avance;

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Avance de obra</h1>
        {t.verTerreno && (
          <Link to="/terreno" className="inline-flex items-center gap-1 text-chico font-medium text-primario hover:underline">
            Ver el terreno
            <span aria-hidden>→</span>
          </Link>
        )}
      </Row>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      <Alerta texto={t.error} />

      {a && !t.hayTareas && (
        <Card>
          {t.esAdmin ? (
            <p className="text-texto-suave">
              {"Para publicar el avance, cargá la duración de las tareas en "}
              <Link to="/cronograma" className="font-medium text-primario underline">
                Obra → Cronograma
              </Link>
              {" o los hitos de la obra en "}
              <Link to={RUTA_HITOS} className="font-medium text-primario underline">
                Configuración → Avance de obra
              </Link>
              .
            </p>
          ) : (
            <p className="text-texto-suave">Todavía no hay avance publicado.</p>
          )}
        </Card>
      )}

      {a && t.hayTareas && t.completado && (
        <Card>
          <Stack espacio={4}>
            <Row justificar="entre" alinear="fin" espacio={4}>
              <Stack espacio={1}>
                <span className="text-chico text-texto-suave">Avance de la obra</span>
                <span className="text-cifra font-extralight tracking-[-0.04em] tabular-nums">{`${t.completado.porcentaje} %`}</span>
              </Stack>
              <span className="text-right text-chico text-texto-suave">{`${t.resumen.completas} de ${t.resumen.total} etapas completas`}</span>
            </Row>
            <BarraProgreso porcentaje={t.completado.porcentaje} tono="primario" etiqueta="Obra completada" alto="grande" />
            {t.resumen.actual && (
              <p>
                <span className="text-texto-suave">Ahora: </span>
                <span className="font-semibold">{t.resumen.actual.nombre}</span>
              </p>
            )}
          </Stack>
        </Card>
      )}

      {a && t.paso && (
        <Card>
          <Stack espacio={4}>
            <Stack espacio={1}>
              <h2 className="text-titulo font-light tracking-tight">Recorrido de la obra</h2>
              <p className="text-chico text-texto-suave">Desde la firma del terreno hasta la entrega. Tocá cada paso para ver qué incluye.</p>
              {t.esAdmin && t.porHitos && (
                <p className="text-chico text-texto-suave">
                  {"En cada hito marcás los cumplidos. Los hitos se cargan y ordenan en "}
                  <Link to={RUTA_HITOS} className="text-primario underline">
                    Configuración → Avance de obra
                  </Link>
                  .
                </p>
              )}
            </Stack>
            <Recorrido etiqueta="Recorrido de la obra" pasos={t.nodos} seleccionado={t.seleccionado} actual={t.actual} onSeleccionar={t.elegirPaso} idPanel="detalle-paso" />
            <Alerta texto={t.errorNoCorresponde} />
            <DetallePaso
              id="detalle-paso"
              fid={t.fid}
              paso={t.paso}
              numero={t.numeroPaso}
              total={t.totalPasos}
              novedades={t.novedadesPaso}
              cargandoNovedades={t.cargandoNovedadesPaso}
              errorNovedades={t.errorNovedadesPaso}
              onAnterior={t.anterior}
              onSiguiente={t.siguiente}
              acciones={t.esAdmin ? { onCumplir: t.abrirCumplir, onDesmarcar: t.abrirDesmarcar, onNoCorresponde: t.noCorresponde } : undefined}
            />
          </Stack>
        </Card>
      )}

      {t.admin && (
        <ControlInterno
          admin={t.admin}
          hayTareas={t.hayTareas}
          onNuevaNovedad={() => t.abrirNovedad(null)}
          onInicio={t.abrirInicio}
          rutaDescargaGantt={t.rutaDescargaGantt}
          onSubirGantt={t.subirGantt}
          onBorrarGantt={t.borrarGantt}
          subiendoGantt={t.subiendoGantt}
          errorGantt={t.errorGantt}
        />
      )}

      {a && (
        <Stack espacio={3}>
          <h2 className="text-titulo font-light tracking-tight">Novedades</h2>
          <Alerta texto={t.errorNovedades} />
          {t.novedades.length === 0 && !t.errorNovedades && <p className="text-texto-suave">Todavía no hay novedades.</p>}
          {t.novedades.length > 0 && (
            <ul aria-label="Novedades" className="flex flex-col gap-3">
              {t.novedades.map((n) => (
                <li key={n.id}>
                  <TarjetaNovedad fid={t.fid} novedad={n} onEditar={t.esAdmin ? () => t.abrirNovedad(n) : undefined} onBorrar={t.esAdmin ? () => t.abrirBorrar(n) : undefined} />
                </li>
              ))}
            </ul>
          )}
          <Alerta texto={t.errorMas} />
          {t.hayMas && (
            <div>
              <Button variante="secundario" cargando={t.cargandoMas} onClick={() => void t.verMas()}>
                Ver más novedades
              </Button>
            </div>
          )}
        </Stack>
      )}

      <Dialog
        abierto={t.dialogo?.tipo === "novedad"}
        onCambio={(abierto) => !abierto && t.cerrarYRecargar()}
        titulo={t.dialogo?.tipo === "novedad" && t.dialogo.novedad ? "Editar novedad" : "Nueva novedad"}
        ancho="amplio"
      >
        {t.dialogo?.tipo === "novedad" && t.admin && (
          <FormularioNovedad fid={t.fid} novedad={t.dialogo.novedad} admin={t.admin} onCancelar={t.cerrarYRecargar} onGuardado={t.cerrarYRecargar} />
        )}
      </Dialog>

      <Dialog abierto={t.dialogo?.tipo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar novedad">
        {t.dialogo?.tipo === "borrar" && (
          <Stack espacio={4}>
            <p>{`¿Borrar "${t.dialogo.novedad.titulo}"? Se borran también sus fotos y los cambios de estado que cargó. No se puede deshacer.`}</p>
            <Alerta texto={t.errorBorrar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
                Borrar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>

      <Dialog abierto={t.dialogo?.tipo === "inicio"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={t.admin?.inicio ? "Cambiar fecha de inicio" : "Fijar fecha de inicio"}>
        {t.dialogo?.tipo === "inicio" && <FormularioInicio fid={t.fid} inicial={t.admin?.inicio ?? null} onCancelar={t.cerrar} onGuardado={t.cerrarYRecargar} />}
      </Dialog>

      <Dialog abierto={t.dialogo?.tipo === "cumplir"} onCambio={(abierto) => !abierto && t.cerrarYRecargar()} titulo="Marcar hito cumplido" ancho="amplio">
        {t.dialogo?.tipo === "cumplir" && (
          <FormularioCumplido fid={t.fid} hitoId={t.dialogo.hitoId} nombre={t.dialogo.nombre} avisar={t.dialogo.avisar} onCancelar={t.cerrarYRecargar} onGuardado={t.cerrarYRecargar} />
        )}
      </Dialog>

      <Dialog abierto={t.dialogo?.tipo === "desmarcar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Desmarcar hito">
        {t.dialogo?.tipo === "desmarcar" && (
          <Stack espacio={4}>
            <p>{`¿Volver "${t.dialogo.nombre}" a pendiente? Se borra la novedad que lo anunció, con sus fotos. No se puede deshacer.`}</p>
            <Alerta texto={t.errorDesmarcar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={t.desmarcando} onClick={() => void t.confirmarDesmarcar()}>
                Desmarcar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>

    </Stack>
  );
}
